import React, { useState, useEffect } from "react";
import { Download, Filter, Calendar, List, FileText as Building, Tag, Users, Globe } from "react-feather";
import { Table, Button, Dropdown, Menu, Popover } from "antd";
import SelectComponent from "react-select";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-daterangepicker/daterangepicker.css";
import { useTranslation } from "react-i18next";
import moment from "moment";
import Swal from "sweetalert2";
import axios from "axios";
import exportService from "../../utils/exportService";
import PrintReportPreview from "../../reports/PrintReportPreview";
import DateRangeSummary from "./roomsold/DateRangeSummary";
import RoomTypeWise from "./roomsold/RoomTypeWise";
import CorporateGroupSales from "./roomsold/CorporateGroupSales";
import DailyRoomSold from "./roomsold/DailyRoomSold";
import RatePlanWise from "./roomsold/RatePlanWise";
import ByGuestBookingSource from "./roomsold/ByGuestBookingSource";
import WalkInVsOta from "./roomsold/WalkInVsOta";
import BookingTypeSummary from "./roomsold/ByGuestBookingSource";

// Mock Data (unchanged)
const mockData = {
  success: true,
  report: [
    {
      "Room No": "6",
      "Room Type": "Presidential Suite",
      "Booking ID": "BKG025",
      "Booking Source": "Direct",
      "Check-in": "2025-06-07",
      "Check-out": "2025-06-08",
      "Rate Per Night": 2000,
      "Discount": 0,
      "Price Sold": 2000,
      "Reservation Type": "Confirmed",
      "Status": "Checked-in",
      "Floor": "1st",
      "Created By": "admin",
      "Tax Amount": 0,
      "Tax Breakdown": [],
      "Pax (Adults/Children)": "1/0",
      "Plan": "EP",
      "Room Rent": 2000
    },
    {
      "Room No": "6",
      "Room Type": "Presidential Suite",
      "Booking ID": "BKG026",
      "Booking Source": "Direct",
      "Check-in": "2025-06-07",
      "Check-out": "2025-06-08",
      "Rate Per Night": 2000,
      "Discount": 0,
      "Price Sold": 2000,
      "Reservation Type": "Confirmed",
      "Status": "Checked-in",
      "Floor": "1st",
      "Created By": "admin",
      "Tax Amount": 0,
      "Tax Breakdown": [],
      "Pax (Adults/Children)": "1/0",
      "Plan": "EP",
      "Room Rent": 2000
    },
    {
      "Room No": "6",
      "Room Type": "Presidential Suite",
      "Booking ID": "BKG027",
      "Booking Source": "Direct",
      "Check-in": "2025-06-07",
      "Check-out": "2025-06-08",
      "Rate Per Night": 2000,
      "Discount": 0,
      "Price Sold": 2000,
      "Reservation Type": "Confirmed",
      "Status": "Checked-in",
      "Floor": "1st",
      "Created By": "admin",
      "Tax Amount": 0,
      "Tax Breakdown": [],
      "Pax (Adults/Children)": "1/0",
      "Plan": "EP",
      "Room Rent": 2000
    }
  ],
  applicableTaxes: ["Luxury Tax"],
  pagination: {
    page: 1,
    pageSize: 3,
    totalRecords: 3,
    totalPages: 1
  }
};

// Mock Metadata (unchanged)
const mockMetadata = {
  roomTypes: [
    { type_name: "Presidential Suite" },
    { type_name: "Deluxe" },
    { type_name: "Suite" },
    { type_name: "Standard" },
    { type_name: "Executive Suite" }
  ],
  floors: [
    { floor_name: "1st" },
    { floor_name: "2nd" },
    { floor_name: "3rd" }
  ],
  sources: [
    { name: "Booking.com" },
    { name: "Expedia" },
    { name: "Agoda" },
    { name: "Direct" },
    { name: "Corporate" }
  ],
  statuses: [
    { name: "Checked-in" },
    { name: "Checked-out" },
    { name: "No-show" },
    { name: "Cancelled" }
  ],
  users: [
    { username: "admin" },
    { username: "staff1" },
    { username: "staff2" }
  ]
};

const RoomSoldReport = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState("daily_room_sold");

  const getBusinessDate = () => {
    const storedBusinessDate = localStorage.getItem("business_date");
    return storedBusinessDate && moment(storedBusinessDate).isValid()
      ? moment(storedBusinessDate)
      : moment(); // Fallback to current date
  };

  const [dateRange, setDateRange] = useState({
    startDate: getBusinessDate(),
    endDate: getBusinessDate(),
  });
  const [singleDate, setSingleDate] = useState(getBusinessDate());
  const [filters, setFilters] = useState({
    roomNo: "",
    roomType: [],
    bookingSource: null,
    reservationStatus: null,
    createdBy: null,
  });
  const [soldRooms, setSoldRooms] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [bookingSources, setBookingSources] = useState([]);
  const [reservationStatuses, setReservationStatuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [applicableTaxes, setApplicableTaxes] = useState([]);
  const [filterVisible, setFilterVisible] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [exportData, setExportData] = useState([]);
  const [exportHeaders, setExportHeaders] = useState([]);
  const [exportFooter, setExportFooter] = useState([]);
  const [totalsForPageFooter, setTotalsForPageFooter] = useState([]);
  const [totalColumns, setTotalColumns] = useState([]);
  const [reportTitle, setReportTitle] = useState("");
  const currency = localStorage.getItem("currencycode") || "₹";
  const apiURL = process.env.REACT_APP_API_URL || "http://localhost:4000";

  // Reset export states on view change
  useEffect(() => {
    setExportData([]);
    setExportHeaders([]);
    setExportFooter([]);
    setTotalsForPageFooter([]);
    setTotalColumns([]);
    setReportTitle("");
    setPreviewVisible(false);
  }, [viewMode]);

  // Fetch data function
  const fetchData = async () => {
    setLoading(true);
    try {
      if (viewMode === "daily_room_sold") {
        // For daily_room_sold, fetch data as before or leave to child component
        console.log(`Fetching data for daily_room_sold: ${singleDate.format("YYYY-MM-DD")}`);
        // Add your API call for daily_room_sold if needed
      } else {
        console.log(
          `Fetching data with startDate: ${dateRange.startDate.format("YYYY-MM-DD")}, endDate: ${dateRange.endDate.format("YYYY-MM-DD")}`
        );
        const token = localStorage.getItem("token");
        const response = await axios.get(`${apiURL}/reports-daily/date-wise-summary`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            startDate: dateRange.startDate.format("YYYY-MM-DD"),
            endDate: dateRange.endDate.format("YYYY-MM-DD"),
            page: 1,
            pageSize: 100,
          },
        });

        console.log("API Response:", response.data);

        if (response.data.success && Array.isArray(response.data.report)) {
          const formattedData = response.data.report.map((row, index) => ({
            room_id: index,
            room_number: "",
            room_type: "",
            booking_id: "",
            booking_source: "",
            check_in: row.date,
            check_out: row.date,
            nights: 1,
            room_sold_count: row.room_sold_count,
            rate_per_night: parseFloat(row.actual_price) || 0,
            discount: parseFloat(row.discount) || 0,
            total_revenue: parseFloat(row.net_revenue) || 0,
            reservation_type: "",
            status: "Checked-in",
            floor: "",
            created_by: "",
            pax: "",
            plan: "",
            room_charges: parseFloat(row.actual_price) || 0,
            tax: parseFloat(row.tax_amount) || 0,
            ...(Array.isArray(response.data.applicableTaxes)
              ? response.data.applicableTaxes.reduce(
                  (acc, taxName) => ({
                    ...acc,
                    [taxName]: parseFloat(row[taxName]) || 0,
                  }),
                  {}
                )
              : {}),
          }));
          setSoldRooms(formattedData);
          setApplicableTaxes(Array.isArray(response.data.applicableTaxes) ? response.data.applicableTaxes : ["Luxury Tax"]);
          if (formattedData.length === 0) {
            Swal.fire({
              title: t("no_data"),
              text: t("no_room_sold_data"),
              icon: "info",
            });
          }
        } else {
          throw new Error(response.data.message || "Failed to fetch Date Wise Summary Report");
        }
      }
    } catch (error) {
      console.error("Error fetching Date Wise Summary Report:", error);
      Swal.fire({
        title: t("error"),
        text: t("failed_fetch_room_sold_report"),
        icon: "error",
      });
      if (mockData.success && Array.isArray(mockData.report)) {
        const formattedData = mockData.report.map((room, index) => ({
          room_id: index,
          room_number: room["Room No"],
          room_type: room["Room Type"],
          booking_id: room["Booking ID"],
          booking_source: room["Booking Source"],
          check_in: room["Check-in"],
          check_out: room["Check-out"],
          nights: moment(room["Check-out"]).diff(moment(room["Check-in"]), "days"),
          rate_per_night: parseFloat(room["Rate Per Night"]) || 0,
          discount: parseFloat(room["Discount"]) || 0,
          total_revenue: parseFloat(room["Price Sold"]) || 0,
          reservation_type: room["Reservation Type"],
          status: room["Status"],
          floor: room["Floor"],
          created_by: room["Created By"],
          pax: room["Pax (Adults/Children)"],
          plan: room["Plan"],
          room_sold_count: room["room_sold_count"],
          room_charges: parseFloat(room["Room Rent"]) || 0,
          tax: parseFloat(room["Tax Amount"]) || 0,
          ...(Array.isArray(room["Tax Breakdown"])
            ? room["Tax Breakdown"].reduce(
                (acc, tax) => ({
                  ...acc,
                  [tax.taxName]: tax.taxAmount || 0,
                }),
                {}
              )
            : {}),
        }));
        setSoldRooms(formattedData);
        setApplicableTaxes(Array.isArray(mockData.applicableTaxes) ? mockData.applicableTaxes : ["Luxury Tax"]);
        if (formattedData.length === 0) {
          Swal.fire({
            title: t("no_data"),
            text: t("no_room_sold_data"),
            icon: "info",
          });
        }
      } else {
        setSoldRooms([]);
      }
    } finally {
      setLoading(false);
    }
  };

  // Initial metadata and data fetch
  useEffect(() => {
    setRoomTypes(mockMetadata.roomTypes.map((type) => type.type_name));
    setBookingSources(mockMetadata.sources.map((source) => source.name));
    setReservationStatuses(mockMetadata.statuses.map((status) => status.name));
    setUsers(mockMetadata.users.map((user) => user.username));
    fetchData();
  }, []);

  // Fetch data on dateRange or viewMode change
  useEffect(() => {
    console.log(
      `useEffect triggered with dateRange: ${dateRange.startDate.format("YYYY-MM-DD")} to ${dateRange.endDate.format("YYYY-MM-DD")}`
    );
    fetchData();
  }, [dateRange.startDate, dateRange.endDate, viewMode, t]);

  // Filter data for non-Daily views
  const filteredRooms = soldRooms.filter((room) => {
    const matchesRoomNo = filters.roomNo
      ? room.room_number.toLowerCase().includes(filters.roomNo.toLowerCase())
      : true;
    const matchesRoomType = filters.roomType.length > 0
      ? filters.roomType.some((type) => type.value === room.room_type)
      : true;
    const matchesBookingSource = filters.bookingSource
      ? room.booking_source === filters.bookingSource.value
      : true;
    const matchesStatus = filters.reservationStatus
      ? room.status === filters.reservationStatus.value
      : true;
    const matchesCreatedBy = filters.createdBy
      ? room.created_by === filters.createdBy.value
      : true;
    return (
      matchesRoomNo &&
      matchesRoomType &&
      matchesBookingSource &&
      matchesStatus &&
      matchesCreatedBy
    );
  });

  // View options with React Feather icons
  const viewOptions = [
    { value: "daily_room_sold", label: t("daily_room_sold"), icon: <Calendar size={16} /> },
    { value: "date_range_summary", label: t("date_range_summary"), icon: <List size={16} /> },
    { value: "room_type_wise", label: t("room_type_wise"), icon: <Tag size={16} /> },
    { value: "rate_plan_wise", label: t("rate_plan_wise"), icon: <Tag size={16} /> },
    { value: "booking_type_summary", label: t("booking_type_summary"), icon: <Users size={16} /> },
  ];

  // Filter options
  const roomTypeOptions = roomTypes.map((type) => ({ value: type, label: type }));
  const bookingSourceOptions = bookingSources.map((source) => ({ value: source, label: source }));
  const reservationStatusOptions = reservationStatuses.map((status) => ({ value: status, label: status }));
  const userOptions = users.map((user) => ({ value: user, label: user }));

  // Filter content for Popover
  const filterContent = (
    <div style={{ width: "400px", padding: "10px" }}>
      <div className="mb-3">
        <label className="block text-sm font-medium text-gray-700 mb-1">{t("room_no")}</label>
        <input
          type="text"
          className="form-control"
          value={filters.roomNo}
          onChange={(e) => setFilters({ ...filters, roomNo: e.target.value })}
          placeholder={t("search_room_no")}
        />
      </div>
      <div className="mb-3">
        <label className="block text-sm font-medium text-gray-700 mb-1">{t("room_type")}</label>
        <SelectComponent
          isMulti
          options={roomTypeOptions}
          value={filters.roomType}
          onChange={(selected) => setFilters({ ...filters, roomType: selected || [] })}
          placeholder={t("select_room_types")}
          styles={{ control: (base) => ({ ...base, borderRadius: "4px", boxShadow: "0 1px 2px rgba(0,0,0,0.1)" }) }}
        />
      </div>
      <div className="mb-3">
        <label className="block text-sm font-medium text-gray-700 mb-1">{t("booking_source")}</label>
        <SelectComponent
          options={bookingSourceOptions}
          value={filters.bookingSource}
          onChange={(selected) => setFilters({ ...filters, bookingSource: selected })}
          isClearable
          placeholder={t("select_booking_source")}
          styles={{ control: (base) => ({ ...base, borderRadius: "4px", boxShadow: "0 1px 2px rgba(0,0,0,0.1)" }) }}
        />
      </div>
      <div className="mb-3">
        <label className="block text-sm font-medium text-gray-700 mb-1">{t("reservation_status")}</label>
        <SelectComponent
          options={reservationStatusOptions}
          value={filters.reservationStatus}
          onChange={(selected) => setFilters({ ...filters, reservationStatus: selected })}
          isClearable
          placeholder={t("select_status")}
          styles={{ control: (base) => ({ ...base, borderRadius: "4px", boxShadow: "0 1px 2px rgba(0,0,0,0.1)" }) }}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t("created_by")}</label>
        <SelectComponent
          options={userOptions}
          value={filters.createdBy}
          onChange={(selected) => setFilters({ ...filters, createdBy: selected })}
          isClearable
          placeholder={t("select_user")}
          styles={{ control: (base) => ({ ...base, borderRadius: "4px", boxShadow: "0 1px 2px rgba(0,0,0,0.1)" }) }}
        />
      </div>
    </div>
  );

  // Handle date range change
  const handleDateRangeChange = (event, picker) => {
    const newStartDate = moment(picker.startDate).startOf("day");
    const newEndDate = moment(picker.endDate).startOf("day");
    if (!newStartDate.isValid() || !newEndDate.isValid()) {
      Swal.fire({ title: t("error"), text: t("invalid_date_selected"), icon: "error" });
      return;
    }
    if (newStartDate.isAfter(newEndDate)) {
      Swal.fire({ title: t("error"), text: t("start_date_after_end_date"), icon: "error" });
      return;
    }
    console.log(
      `Date Range Selected: ${newStartDate.format("YYYY-MM-DD")} to ${newEndDate.format("YYYY-MM-DD")}`
    );
    setDateRange({ startDate: newStartDate, endDate: newEndDate });
  };

  // Handle single date change
  const handleSingleDateChange = (action) => {
    let newDate;
    if (action === "prev") {
      newDate = moment(singleDate).subtract(1, "days");
    } else if (action === "next") {
      newDate = moment(singleDate).add(1, "days");
    }
    console.log(`Single Date Changed: ${newDate.format("YYYY-MM-DD")}`);
    setSingleDate(newDate);
  };

  // Export and preview handling
  const handleExport = (format) => {
    if (!exportData.length && format !== "print") {
      Swal.fire({
        title: t("error"),
        text: t("no_data_available_for_export"),
        icon: "error",
      });
      return;
    }
    const filename =
      viewMode === "daily_room_sold"
        ? `room_sold_report_${singleDate.format("YYYY-MM-DD")}_${new Date()
            .toISOString()
            .replace(/[:.-]/g, "")}`
        : `room_sold_report_${dateRange.startDate.format("YYYY-MM-DD")}_${dateRange.endDate.format(
            "YYYY-MM-DD"
          )}`;
    if (format === "xls") {
      exportService.exportToXLS(exportData, exportHeaders, `${filename}.xlsx`, reportTitle);
    } else if (format === "csv") {
      exportService.exportToCSV(exportData, exportHeaders, `${filename}.csv`);
    } else if (format === "pdf") {
      exportService.exportToPDF(
        exportData,
        exportHeaders,
        reportTitle,
        `${filename}.pdf`,
        false,
        true,
        false,
        exportFooter
      );
    } else if (format === "preview") {
      if (exportData.length === 0) {
        Swal.fire({
          title: t("error"),
          text: t("no_data_available_for_preview"),
          icon: "error",
        });
        return;
      }
      setPreviewVisible(true);
    } else if (format === "print") {
      window.print();
    }
  };

  // Download menu with Preview option
  const downloadMenu = (
    <Menu>
      <Menu.Item key="xls" onClick={() => handleExport("xls")}>
        {t("export_xls")}
      </Menu.Item>
      <Menu.Item key="csv" onClick={() => handleExport("csv")}>
        {t("export_csv")}
      </Menu.Item>

      <Menu.Item key="preview" onClick={() => handleExport("preview")}>
        {t("preview")}
      </Menu.Item>

    </Menu>
  );

  // Render view based on viewMode
  const renderView = () => {
    switch (viewMode) {
      case "daily_room_sold":
        return (
          <DailyRoomSold
            selectedDate={singleDate}
            setSelectedDate={setSingleDate}
            setExportData={setExportData}
            setExportHeaders={setExportHeaders}
            setExportFooter={setExportFooter}
            setTotalsForPageFooter={setTotalsForPageFooter}
            setTotalColumns={setTotalColumns}
            setReportTitle={setReportTitle}
            parentFilters={filters}
          />
        );
      case "date_range_summary":
        return (
          <DateRangeSummary
            rooms={filteredRooms}
            applicableTaxes={applicableTaxes}
            dateRange={dateRange}
            setExportData={setExportData}
            setExportHeaders={setExportHeaders}
            setExportFooter={setExportFooter}
            setTotalsForPageFooter={setTotalsForPageFooter}
            setTotalColumns={setTotalColumns}
            setReportTitle={setReportTitle}
          />
        );
      case "room_type_wise":
        return (
          <RoomTypeWise
            dateRange={dateRange}
            setExportData={setExportData}
            setExportHeaders={setExportHeaders}
            setExportFooter={setExportFooter}
            setTotalsForPageFooter={setTotalsForPageFooter}
            setTotalColumns={setTotalColumns}
            setReportTitle={setReportTitle}
          />
        );
      case "corporate_group_sales":
        return (
          <CorporateGroupSales
            rooms={filteredRooms}
            applicableTaxes={applicableTaxes}
            dateRange={dateRange}
            setExportData={setExportData}
            setExportHeaders={setExportHeaders}
            setExportFooter={setExportFooter}
            setTotalsForPageFooter={setTotalsForPageFooter}
            setTotalColumns={setTotalColumns}
            setReportTitle={setReportTitle}
          />
        );
      case "rate_plan_wise":
        return (
          <RatePlanWise
                         dateRange={dateRange}
             setExportData={setExportData}
             setExportHeaders={setExportHeaders}
             setExportFooter={setExportFooter}
             setTotalsForPageFooter={setTotalsForPageFooter}
             setTotalColumns={setTotalColumns}
             setReportTitle={setReportTitle}

          />
        );
      case "booking_type_summary":
        return (
          <BookingTypeSummary
            dateRange={dateRange}
            setExportData={setExportData}
            setExportHeaders={setExportHeaders}
            setExportFooter={setExportFooter}
            setTotalsForPageFooter={setTotalsForPageFooter}
            setTotalColumns={setTotalColumns}
            setReportTitle={setReportTitle}
          />
        );
      case "walk_in_vs_ota":
        return (
          <WalkInVsOta
            rooms={filteredRooms}
            applicableTaxes={applicableTaxes}
            setExportData={setExportData}
            setExportHeaders={setExportHeaders}
            setExportFooter={setExportFooter}
            setTotalsForPageFooter={setTotalsForPageFooter}
            setTotalColumns={setTotalColumns}
            setReportTitle={setReportTitle}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="page-header page-top-icons px-3 py-2 bg-white border rounded">
          <div className="row align-items-center flex-nowrap">
            <div className="col-auto d-flex align-items-center gap-3">
              <h2
                className="room-title mb-0"
                style={{
                  fontSize: "20px",
                  fontWeight: "600",
                  width: "400px",
                  borderBottom: "2px solid #007bff",
                  paddingBottom: "4px",
                }}
              >
                {t("ROOM_SOLD")}
              </h2>
              {viewMode === "daily_room_sold" ? (
                <div className="d-flex align-items-center">
                  <Button
                    icon={<i className="fa fa-chevron-circle-left" />}
                    onClick={() => handleSingleDateChange("prev")}
                    className="me-2"
                    style={{ height: "38px" }}
                  />
                  <span className="fw-bold fs-6" style={{ width: "120px", textAlign: "center" }}>
                    {singleDate.format("DD MMM YYYY")}
                  </span>
                  <Button
                    icon={<i className="fa fa-chevron-circle-right" />}
                    onClick={() => handleSingleDateChange("next")}
                    className="ms-2"
                    style={{ height: "38px" }}
                  />
                </div>
              ) : (
                <DateRangePicker
                  key={`${dateRange.startDate.format("YYYY-MM-DD")}-${dateRange.endDate.format("YYYY-MM-DD")}`}
                  initialSettings={{
                    startDate: dateRange.startDate.toDate(),
                    endDate: dateRange.endDate.toDate(),
                    ranges: {
                      Today: [moment().startOf("day").toDate(), moment().endOf("day").toDate()],
                      Yesterday: [
                        moment().subtract(1, "days").startOf("day").toDate(),
                        moment().subtract(1, "days").endOf("day").toDate(),
                      ],
                      "This Week": [moment().startOf("week").toDate(), moment().endOf("week").toDate()],
                      "Last Week": [
                        moment().subtract(1, "week").startOf("week").toDate(),
                        moment().subtract(1, "week").endOf("week").toDate(),
                      ],
                      "This Month": [moment().startOf("month").toDate(), moment().endOf("month").toDate()],
                      "Last Month": [
                        moment().subtract(1, "month").startOf("month").toDate(),
                        moment().subtract(1, "month").endOf("month").toDate(),
                      ],
                    },
                    locale: { format: "DD MMM YYYY", applyLabel: "Apply", cancelLabel: "Cancel" },
                    alwaysShowCalendars: true,
                    autoApply: false,
                    linkedCalendars: false,
                  }}
                  onApply={handleDateRangeChange}
                >
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control text-center fw-bold"
                      value={`${dateRange.startDate.format("DD MMM YYYY")} - ${dateRange.endDate.format(
                        "DD MMM YYYY"
                      )}`}
                      style={{
                        height: "38px",
                        fontSize: "15px",
                        borderRight: "none",
                        backgroundColor: "#ffffff",
                        width: "220px",
                      }}
                      readOnly
                    />
                    <div className="input-group-append">
                      <span
                        className="input-group-text"
                        style={{
                          backgroundColor: "#fff",
                          borderLeft: "none",
                          borderRadius: "0 4px 4px 0",
                          cursor: "pointer",
                          height: "38px",
                        }}
                      >
                        <Calendar size={16} style={{ color: "#495057" }} />
                      </span>
                    </div>
                  </div>
                </DateRangePicker>
              )}
            </div>
            <div className="col-auto ms-auto d-flex align-items-center gap-3">
              <div className="dropdown">
                <button
                  className="btn btn-outline-primary dropdown-toggle d-flex align-items-center"
                  type="button"
                  id="roomSoldViewsDropdown"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  style={{ height: "38px", fontSize: "14px" }}
                >
                  <List size={16} className="me-2" />
                  {viewOptions.find((opt) => opt.value === viewMode)?.label || t("room_sold_views")}
                </button>
                <ul className="dropdown-menu" aria-labelledby="roomSoldViewsDropdown">
                  {viewOptions.map((option) => (
                    <li key={option.value}>
                      <button
                        className={`dropdown-item d-flex align-items-center ${
                          viewMode === option.value ? "active" : ""
                        }`}
                        onClick={() => setViewMode(option.value)}
                      >
                        {option.icon}
                        <span className="ms-2">{option.label}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              <Popover
                content={filterContent}
                title={<span className="font-medium">{t("filters")}</span>}
                trigger="click"
                open={filterVisible}
                onOpenChange={setFilterVisible}
                placement="bottomRight"
              >
                <Button type="primary" className="d-flex align-items-center" style={{ height: "38px" }}>
                  <Filter size={16} className="me-2" /> {t("filter")}
                </Button>
              </Popover>
              <Dropdown overlay={downloadMenu} trigger={["click"]} placement="bottomRight">
                <Button type="primary" className="d-flex align-items-center" style={{ height: "38px" }}>
                  <Download size={16} className="me-2" /> {t("download")}
                </Button>
              </Dropdown>
            </div>
          </div>
        </div>
        {loading ? (
          <div className="text-center my-5">
            <div className="loader">
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
            </div>
          </div>
        ) : (
          renderView()
        )}
        <PrintReportPreview
          visible={previewVisible}
          onClose={() => setPreviewVisible(false)}
          reportTitle={reportTitle}
          propertyName=""
          data={exportData}
          headers={exportHeaders}
          footerData={''}
          totalColumns={totalColumns}
          apiEndpoint="transactions/generate-pdf"
          apiUrl={apiURL}
        />
        <style jsx>{`
          .loader {
            display: flex;
            justify-content: center;
            align-items: center;
          }
          .dot {
            width: 10px;
            height: 10px;
            background-color: #007bff;
            border-radius: 50%;
            margin: 0 5px;
            animation: dot-bounce 1.4s infinite ease-in-out both;
          }
          .dot:nth-child(1) {
            animation-delay: -0.32s;
          }
          .dot:nth-child(2) {
            animation-delay: -0.16s;
          }
          @keyframes dot-bounce {
            0%,
            80%,
            100% {
              transform: scale(0);
            }
            40% {
              transform: scale(1);
            }
          }
          .form-control {
            border-radius: 4px;
            border: 1px solid #d9d9d9;
            padding: 6px 12px;
            font-size: 14px;
          }
          .dropdown-menu .active {
            background-color: #007bff;
            color: white;
          }
        `}</style>
      </div>
    </div>
  );
};

export default RoomSoldReport;